/* Cart IČ/DIČ — ARES autofill + cart.attributes write
 * No build step, vanilla ES2017. Loaded on cart routes only.
 */
(function () {
  'use strict';

  const ARES_BASE = 'https://ares.gov.cz/ekonomicke-subjekty-v-be/rest/ekonomicke-subjekty/';
  // If P1 smoke test failed, override via window.JACKERY_ARES_PROXY = 'https://xml.ilico.io/ares/'
  const aresUrl = (ico) => (window.JACKERY_ARES_PROXY || ARES_BASE) + encodeURIComponent(ico);
  const CACHE_TTL_MS = 24 * 60 * 60 * 1000;
  const REQUEST_TIMEOUT_MS = 3000;

  // --- IČ checksum (modulo 11, official MF ČR algorithm) ---
  // Returns user-typed digits, max 8 chars. NO padding —
  // user-visible value stays exactly what they typed.
  function normalizeIc(input) {
    return String(input || '').replace(/\D/g, '').slice(0, 8);
  }

  function isValidIcChecksum(ic) {
    if (!/^\d{8}$/.test(ic)) return false;
    const weights = [8, 7, 6, 5, 4, 3, 2];
    let sum = 0;
    for (let i = 0; i < 7; i++) sum += parseInt(ic[i], 10) * weights[i];
    const mod = sum % 11;
    let c;
    if (mod === 0) c = 1;
    else if (mod === 1) c = 0;
    else if (mod === 10) c = 1;
    else c = 11 - mod;
    return c === parseInt(ic[7], 10);
  }

  // --- ARES with localStorage cache, AbortController, timeout ---
  let currentAbort = null;

  function readCache(ic) {
    try {
      const raw = localStorage.getItem('ares:' + ic);
      if (!raw) return null;
      const obj = JSON.parse(raw);
      if (!obj.ts || Date.now() - obj.ts > CACHE_TTL_MS) return null;
      return obj.data;
    } catch (e) { return null; }
  }

  function writeCache(ic, data) {
    try {
      localStorage.setItem('ares:' + ic, JSON.stringify({ ts: Date.now(), data }));
    } catch (e) {}
  }

  async function lookupAres(ic) {
    const cached = readCache(ic);
    if (cached) return { ok: true, data: cached, fromCache: true };

    if (currentAbort) currentAbort.abort();
    currentAbort = new AbortController();
    const t = setTimeout(() => currentAbort.abort(), REQUEST_TIMEOUT_MS);

    try {
      const res = await fetch(aresUrl(ic), { signal: currentAbort.signal });
      clearTimeout(t);
      if (res.status === 404) return { ok: false, kind: 'not_found' };
      if (!res.ok) return { ok: false, kind: 'server_error', status: res.status };
      const json = await res.json();
      // Note: ARES omits `dic` field entirely for non-VAT subjects (OSVČ neplátci) —
      // safe because `json.dic` returns undefined which is falsy → '' fallback.
      const sidlo = json.sidlo || {};
      const streetParts = [];
      if (sidlo.nazevUlice) streetParts.push(sidlo.nazevUlice);
      if (sidlo.cisloDomovni) {
        let num = String(sidlo.cisloDomovni);
        if (sidlo.cisloOrientacni) num += '/' + sidlo.cisloOrientacni + (sidlo.cisloOrientacniPismeno || '');
        streetParts.push(num);
      }
      // Fallback: parse textovaAdresa (e.g. "Švermova 1322/1b, 78401 Litovel")
      let street = streetParts.join(' ');
      if (!street && sidlo.textovaAdresa) {
        const parts = String(sidlo.textovaAdresa).split(',').map((s) => s.trim());
        if (parts[0]) street = parts[0];
      }
      const data = {
        ic: json.ico || ic,
        company_name: json.obchodniJmeno || '',
        dic: json.dic ? (String(json.dic).startsWith('CZ') ? json.dic : 'CZ' + json.dic) : '',
        address1: street,
        city: sidlo.nazevObce || '',
        zip: String(sidlo.psc || '').replace(/\s/g, ''),
      };
      writeCache(ic, data);
      return { ok: true, data };
    } catch (e) {
      clearTimeout(t);
      if (e.name === 'AbortError') return { ok: false, kind: 'timeout' };
      return { ok: false, kind: 'network_error' };
    }
  }

  // --- cart.attributes write ---
  // Synchronous cache of cart.attributes — populated on page load and after
  // every writeCartAttrs/clearCartAttrs. Used by checkout click handler to
  // sync-preventDefault before the form has a chance to submit. (Per-element
  // binding with `await` before preventDefault was too late — the native form
  // submit fired first and the prefill params never reached the checkout.)
  let __cachedAttrs = null;
  async function refreshCachedAttrs() {
    try {
      const res = await fetch('/cart.js', { headers: { 'Accept': 'application/json' } });
      const cart = await res.json();
      __cachedAttrs = (cart && cart.attributes) || {};
    } catch (e) { __cachedAttrs = null; }
  }

  // Defense-in-depth: strip `<` / `>` from any string value before storing.
  // ARES never returns angle brackets in legitimate CZ company names. Keeps
  // `&`, `'`, `"` intact (legitimate in names like "L'Oréal", "A&K Plus s.r.o.").
  function sanitizeAttrs(attrs) {
    const out = {};
    for (const [k, v] of Object.entries(attrs || {})) {
      out[k] = typeof v === 'string' ? v.replace(/[<>]/g, '') : v;
    }
    return out;
  }

  async function writeCartAttrs(attrs) {
    const res = await fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ attributes: sanitizeAttrs(attrs) }),
    });
    if (res.status === 422) return { ok: true, empty: true }; // empty cart, ignore
    if (!res.ok) return { ok: false };
    try {
      const cart = await res.clone().json();
      __cachedAttrs = (cart && cart.attributes) || {};
    } catch (e) {}
    return { ok: true };
  }

  async function clearCartAttrs() {
    return writeCartAttrs({
      ic: '',
      dic: '',
      company_name: '',
      billing_address1: '',
      billing_city: '',
      billing_zip: '',
    });
  }

  // === DOM init ===
  function t(key, vars) {
    // Tiny Liquid-locale fallback — read from window.JackeryLocale if present, else use key
    const dict = (window.JackeryLocale && window.JackeryLocale.cart && window.JackeryLocale.cart.b2b) || {};
    let s = dict[key] || key;
    if (vars) Object.keys(vars).forEach(k => { s = s.replace(new RegExp('\\{\\{' + k + '\\}\\}', 'g'), vars[k]); });
    return s;
  }

  function setStatus(statusEl, state, text) {
    if (!statusEl) return;
    if (state) statusEl.setAttribute('data-state', state); else statusEl.removeAttribute('data-state');
    statusEl.textContent = text || '';
  }
  function setError(errEl, text) { if (errEl) errEl.textContent = text || ''; }

  // Triggered on blur (NOT on every keystroke).
  // Uses raw user input for display; pads internally for ARES lookup.
  async function handleIcInput(b2bRoot) {
    const input = b2bRoot.querySelector('[data-cart-b2b-ic]');
    const statusEl = b2bRoot.querySelector('.cart-b2b__status');
    const errEl = b2bRoot.querySelector('.cart-b2b__error');
    const companyEl = b2bRoot.querySelector('[data-cart-b2b-company]');
    const dicEl = b2bRoot.querySelector('[data-cart-b2b-dic]');
    const previewEl = b2bRoot.querySelector('[data-cart-b2b-preview]');

    // Raw user input (digits only, max 8). NO padding into input.value —
    // user keeps seeing exactly what they typed.
    const rawIc = window.JackeryB2B.normalizeIc(input.value);

    setError(errEl, '');
    setStatus(statusEl, null, '');
    input.setAttribute('aria-invalid', 'false');

    if (!rawIc) {
      await window.JackeryB2B.clearCartAttrs();
      if (companyEl) companyEl.textContent = '';
      if (dicEl) dicEl.textContent = '';
      if (previewEl) previewEl.hidden = true;
      return;
    }

    // Pad to canonical 8-digit form for checksum + ARES + cart storage.
    // Older companies have IČs with leading zeros (e.g. "01830147"); users
    // commonly omit them. Padding is internal-only, input.value is untouched.
    const ic = rawIc.padStart(8, '0');

    if (!window.JackeryB2B.isValidIcChecksum(ic)) {
      setError(errEl, t('ic_invalid'));
      input.setAttribute('aria-invalid', 'true');
      return;
    }

    setStatus(statusEl, 'loading', t('ares_loading'));
    const result = await window.JackeryB2B.lookupAres(ic);
    if (result.ok) {
      setStatus(statusEl, null, '');
      if (companyEl) companyEl.textContent = result.data.company_name;
      if (dicEl) dicEl.textContent = result.data.dic || t('dic_osvc');
      if (previewEl) {
        previewEl.hidden = false;
        const isDrawer = b2bRoot.dataset.variant === 'drawer';
        const sep = isDrawer ? ' · ' : ', ';
        const addr1 = result.data.address1 || '';
        const city = result.data.city || '';
        const zip = result.data.zip || '';
        const cityZip = (zip || city) ? (zip + (zip && city ? ' ' : '') + city).trim() : '';
        const addrLine = [addr1, cityZip].filter(Boolean).join(', ');
        const addrHtml = addrLine ? '<div class="cart-b2b__preview-addr">' + escapeHtml(addrLine) + '</div>' : '';
        const idsHtml =
          '<div class="cart-b2b__preview-ids">' +
            t('preview_ic', { ic }) +
            (result.data.dic ? sep + t('preview_dic', { dic: result.data.dic }) : '') +
          '</div>';
        const nameHtml = '<div class="cart-b2b__preview-name">' + escapeHtml(result.data.company_name) + '</div>';
        if (isDrawer) {
          previewEl.innerHTML =
            '<span class="cart-b2b__preview-check" aria-hidden="true">✓</span>' +
            '<div class="cart-b2b__preview-body">' + nameHtml + idsHtml + addrHtml + '</div>';
        } else {
          const noteHtml = '<p class="cart-b2b__preview-note">' + escapeHtml(t('preview_note')) + '</p>';
          previewEl.innerHTML =
            '<strong>' + t('preview_label') + '</strong>' + nameHtml + idsHtml + addrHtml + noteHtml;
        }
      }
      // Write to cart from ARES response, NOT from DOM
      const writeRes = await window.JackeryB2B.writeCartAttrs({
        ic,
        dic: result.data.dic || '',
        company_name: result.data.company_name || '',
        billing_address1: result.data.address1 || '',
        billing_city: result.data.city || '',
        billing_zip: result.data.zip || '',
      });
      if (!writeRes.ok) setError(errEl, t('save_failed'));
    } else if (result.kind === 'not_found') {
      setError(errEl, t('ic_not_found', { ic }));
      input.setAttribute('aria-invalid', 'true');
      await window.JackeryB2B.clearCartAttrs();
    } else if (result.kind === 'timeout' || result.kind === 'server_error' || result.kind === 'network_error') {
      setStatus(statusEl, 'info', t('ares_unavailable'));
      // Allow submit; store just IČ (no ARES data)
      await window.JackeryB2B.writeCartAttrs({
        ic,
        dic: '',
        company_name: '',
        billing_address1: '',
        billing_city: '',
        billing_zip: '',
      });
    }
  }

  function escapeHtml(s) {
    return String(s || '').replace(/[&<>"']/g, (c) => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[c]));
  }

  function debounce(fn, ms) {
    let t = null;
    return function () {
      const args = arguments, ctx = this;
      clearTimeout(t);
      t = setTimeout(() => fn.apply(ctx, args), ms);
    };
  }

  function handleToggle(b2bRoot) {
    const toggle = b2bRoot.querySelector('[data-cart-b2b-toggle]');
    const fields = b2bRoot.querySelector('[data-cart-b2b-fields]');
    const preview = b2bRoot.querySelector('[data-cart-b2b-preview]');
    if (toggle.checked) {
      fields.hidden = false;
      toggle.setAttribute('aria-expanded', 'true');
    } else {
      fields.hidden = true;
      if (preview) preview.hidden = true;
      toggle.setAttribute('aria-expanded', 'false');
      window.JackeryB2B.clearCartAttrs();
      const input = b2bRoot.querySelector('[data-cart-b2b-ic]');
      if (input) input.value = '';
    }
  }

  // initB2BRoot is now a no-op (document-level delegation handles everything)
  // but kept as a stable export for the cart-drawer AJAX re-bind hook.
  function initB2BRoot(_b2bRoot) {}

  // === Event delegation ===
  // Per-element listeners died whenever the cart drawer re-rendered via AJAX
  // (the IČ field was replaced, so its blur listener vanished → typing IČ in the
  // drawer did nothing → no ARES lookup → no address). Document-level delegation
  // in the capture phase survives any DOM swap and works in both /cart and drawer.
  let __delegationBound = false;
  function initDelegation() {
    if (__delegationBound) return;
    __delegationBound = true;

    // Toggle change — stopPropagation so it doesn't bubble into the cart's
    // own change handler (which would trigger an unrelated cart re-render).
    document.addEventListener('change', (e) => {
      const toggle = e.target && e.target.closest && e.target.closest('[data-cart-b2b-toggle]');
      if (!toggle) return;
      e.stopPropagation();
      const b2bRoot = toggle.closest('[data-cart-b2b]');
      if (b2bRoot) handleToggle(b2bRoot);
    }, true);

    // IČ input change (fires on blur of text inputs) — also stopPropagation.
    document.addEventListener('change', (e) => {
      const ic = e.target && e.target.closest && e.target.closest('[data-cart-b2b-ic]');
      if (ic) e.stopPropagation();
    }, true);

    // IČ typing — strip non-digits, cap to 8, clear pending error. No validation here.
    document.addEventListener('input', (e) => {
      const icInput = e.target && e.target.closest && e.target.closest('[data-cart-b2b-ic]');
      if (!icInput) return;
      e.stopPropagation();
      const b2bRoot = icInput.closest('[data-cart-b2b]');
      if (!b2bRoot) return;
      const clean = window.JackeryB2B.normalizeIc(icInput.value);
      if (clean !== icInput.value) icInput.value = clean;
      const errEl = b2bRoot.querySelector('.cart-b2b__error');
      const statusEl = b2bRoot.querySelector('.cart-b2b__status');
      setError(errEl, '');
      setStatus(statusEl, null, '');
      icInput.setAttribute('aria-invalid', 'false');
    }, true);

    // IČ blur — trigger ARES lookup (capture phase since blur doesn't bubble).
    document.addEventListener('blur', (e) => {
      const icInput = e.target && e.target.closest && e.target.closest('[data-cart-b2b-ic]');
      if (!icInput) return;
      const b2bRoot = icInput.closest('[data-cart-b2b]');
      if (b2bRoot) handleIcInput(b2bRoot);
    }, true);

    // Enter inside IČ field — ARES lookup + prevent accidental form submit.
    document.addEventListener('keydown', (e) => {
      if (e.key !== 'Enter') return;
      const icInput = e.target && e.target.closest && e.target.closest('[data-cart-b2b-ic]');
      if (!icInput) return;
      e.preventDefault();
      e.stopPropagation();
      const b2bRoot = icInput.closest('[data-cart-b2b]');
      if (b2bRoot) handleIcInput(b2bRoot);
    }, true);
  }

  // === Checkout prefill ===
  // When customer has B2B cart.attributes (from ARES), prefill Shopify
  // checkout's billing_address fields via URL params before redirect.
  function buildCheckoutParams(attrs) {
    const params = new URLSearchParams();
    // ARES data do shipping + billing namespace. Funguje pro first-checkout
    // visit; po změně IČ a re-entry může Shopify session cache udržet stale
    // shipping address fields (company se updatuje, address1/city/zip ne).
    // Toto je platform limit non-Plus modern checkoutu. Real customers
    // typically nemění IČ mezi checkouty → není problém v produkci.
    const fields = {
      company: attrs.company_name,
      address1: attrs.billing_address1,
      city: attrs.billing_city,
      zip: attrs.billing_zip,
      country: 'Czech Republic',
    };
    Object.entries(fields).forEach(([k, v]) => {
      if (!v) return;
      params.set('checkout[shipping_address][' + k + ']', v);
      params.set('checkout[billing_address][' + k + ']', v);
    });
    return params;
  }

  // Document-level checkout interception. Reads the SYNCHRONOUS __cachedAttrs
  // (not an async /cart.js fetch) so e.preventDefault() can fire before the
  // browser dispatches the native form submit / link navigation. This is the
  // core fix: the previous per-element handler awaited a fetch before calling
  // preventDefault, so the checkout opened without the prefill params.
  let __checkoutDelegationBound = false;
  function initCheckoutDelegation() {
    if (__checkoutDelegationBound) return;
    __checkoutDelegationBound = true;

    function redirectWithPrefill(baseUrl) {
      if (!baseUrl || typeof baseUrl !== 'string' || !/^\/(?![\/\\])/.test(baseUrl)) baseUrl = '/checkout';
      const attrs = __cachedAttrs;
      if (!attrs || !attrs.ic || !attrs.company_name) return false;
      const params = buildCheckoutParams(attrs);
      if (![...params].length) return false;
      const sep = baseUrl.includes('?') ? '&' : '?';
      window.location.href = baseUrl + sep + params.toString();
      return true;
    }

    // Click on checkout link (drawer) or submit button (cart page) — capture phase.
    document.addEventListener('click', (e) => {
      const attrs = __cachedAttrs;
      if (!attrs || !attrs.ic || !attrs.company_name) return;

      const link = e.target && e.target.closest && e.target.closest('a[href="/checkout"], a[data-cart-drawer-checkout]');
      if (link) {
        const params = buildCheckoutParams(attrs);
        if (![...params].length) return;
        e.preventDefault();
        redirectWithPrefill(link.getAttribute('href') || '/checkout');
        return;
      }

      const btn = e.target && e.target.closest && e.target.closest('button[name="checkout"], input[name="checkout"]');
      if (btn) {
        if (![...buildCheckoutParams(attrs)].length) return;
        e.preventDefault();
        e.stopPropagation();
        redirectWithPrefill('/checkout');
      }
    }, true);

    // Form submit — backup intercept (Enter-in-field, JS form.submit(), etc.).
    document.addEventListener('submit', (e) => {
      const form = e.target;
      if (!form || !form.querySelector) return;
      if (!form.querySelector('button[name="checkout"], input[name="checkout"]')) return;
      const attrs = __cachedAttrs;
      if (!attrs || !attrs.ic || !attrs.company_name) return;
      e.preventDefault();
      e.stopPropagation();
      redirectWithPrefill('/checkout');
    }, true);
  }

  // Kept as a stable export for the cart-drawer AJAX re-bind hook. Delegation is
  // bound once and survives DOM swaps, so this only needs to refresh the cache.
  function bindCheckoutPrefill() { refreshCachedAttrs(); }

  // Expose for cart-drawer re-bind after AJAX update
  window.__JackeryB2BInitFn = initB2BRoot;
  window.__JackeryBindCheckoutPrefill = bindCheckoutPrefill;

  // Delegation (B2B fields + checkout) binds once at load — survives drawer
  // AJAX re-renders, so no per-element rebinding is ever needed.
  initDelegation();
  initCheckoutDelegation();

  function initAll() { refreshCachedAttrs(); }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
  // After the cart drawer re-renders via AJAX, cart-drawer.js calls
  // window.__JackeryBindCheckoutPrefill() (→ refreshCachedAttrs) to refresh the cache.

  // --- Public API ---
  // initB2BRoot is defined in DOM init block appended in Task 6 — reserved here for export
  window.JackeryB2B = { normalizeIc, isValidIcChecksum, lookupAres, writeCartAttrs, clearCartAttrs, get initB2BRoot() { return window.__JackeryB2BInitFn; } };
})();
